import React, { useEffect, useRef } from "react";
export function DomUpdateHighlighter({ children, debounceMs = 80, shouldHighlight, observerOptions, disabled = false, autoScroll = true, scrollDebounceMs = 300 }) {
    const rootRef = useRef(null);
    useEffect(() => {
        if (disabled)
            return;
        if (!rootRef.current)
            return;
        const root = rootRef.current;
        const pendingElements = new Set();
        let timer = null;
        let scrollTimer = null;
        let lastAffectedElement = null;
        // Add simple CSS animation
        const addAnimationStyles = () => {
            const styleId = 'dom-update-highlighter-styles';
            if (document.getElementById(styleId))
                return;
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        .dom-update-flash {
          animation: dom-update-pulse 1s ease-in-out;
        }
        
        @keyframes dom-update-pulse {
          0% { background-color: rgba(255, 251, 204, 0.8); }
          50% { background-color: rgba(255, 251, 204, 1); }
          100% { background-color: white; }
        }
      `;
            document.head.appendChild(style);
        };
        addAnimationStyles();
        const flashElement = (element) => {
            //Only flash if the element have this atrribute data-fieldname
            if (!element.getAttribute("data-fieldname")) {
                return;
            }
            element.classList.add("dom-update-flash");
            element.addEventListener("animationend", () => element.classList.remove("dom-update-flash"), { once: true });
        };
        const scrollToElement = (element) => {
            if (!autoScroll)
                return;
            //Only scroll if the element have this atrribute data-fieldname
            if (!element.getAttribute("data-fieldname")) {
                return;
            }
            // Try to find a more suitable element to scroll to (input, field container, etc.)
            const scrollTarget = element.querySelector('input, textarea, select') ||
                element.closest('.fluid-field-container, .form-field, [data-field]') ||
                element;
            // Always scroll, let the browser handle if it's already in view
            try {
                scrollTarget.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
            catch (error) {
                // Fallback: scroll to original element
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        };
        const scheduleScroll = () => {
            if (!autoScroll) {
                return;
            }
            if (!lastAffectedElement) {
                return;
            }
            if (scrollTimer) {
                clearTimeout(scrollTimer);
            }
            scrollTimer = window.setTimeout(() => {
                if (lastAffectedElement) {
                    scrollToElement(lastAffectedElement);
                }
                scrollTimer = null;
            }, scrollDebounceMs);
        };
        const processPendingElements = () => {
            const elements = Array.from(pendingElements);
            pendingElements.clear();
            elements.forEach(flashElement);
        };
        const scheduleFlash = (element) => {
            //Only flash if the element have this atrribute data-fieldname
            if (!element.getAttribute("data-fieldname")) {
                return;
            }
            pendingElements.add(element);
            // Track the most recent element, but try to find a better target for scrolling
            const scrollCandidate = element.closest('.fluid-field-container, .form-field, [data-field]') ||
                element.querySelector('input, textarea, select') ||
                element;
            lastAffectedElement = scrollCandidate;
            if (timer)
                return;
            timer = window.setTimeout(() => {
                timer = null;
                requestAnimationFrame(() => {
                    processPendingElements();
                    scheduleScroll(); // Schedule scroll after processing elements
                });
            }, debounceMs);
        };
        const obs = new MutationObserver((mutations) => {
            for (const m of mutations) {
                // 1. Handle added nodes (new items)
                if (m.type === "childList" && m.addedNodes.length) {
                    m.addedNodes.forEach((n) => {
                        if (n.nodeType === 1) {
                            const el = n;
                            if (el.closest("[data-no-highlight]"))
                                return;
                            if (shouldHighlight && !shouldHighlight(el, m))
                                return;
                            scheduleFlash(el);
                        }
                    });
                }
                // 2. Handle text content changes (value changes)
                if (m.type === "characterData") {
                    const parentElement = m.target.parentElement;
                    if (parentElement && parentElement !== root &&
                        !parentElement.closest("[data-no-highlight]")) {
                        if (!shouldHighlight || shouldHighlight(parentElement, m)) {
                            scheduleFlash(parentElement);
                        }
                    }
                }
                // 3. Handle value attribute changes (input field values)
                if (m.type === "attributes" && m.attributeName === "value") {
                    const element = m.target;
                    if (element && element !== root &&
                        !element.closest("[data-no-highlight]")) {
                        if (!shouldHighlight || shouldHighlight(element, m)) {
                            scheduleFlash(element);
                        }
                    }
                }
            }
        });
        obs.observe(root, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['value'], // Only watch for value attribute changes
            ...observerOptions,
        });
        return () => {
            obs.disconnect();
            if (timer != null)
                clearTimeout(timer);
            if (scrollTimer != null)
                clearTimeout(scrollTimer);
            pendingElements.clear();
            lastAffectedElement = null;
        };
    }, [debounceMs, shouldHighlight, observerOptions, disabled, autoScroll, scrollDebounceMs]);
    return React.createElement("div", { ref: rootRef }, children);
}
